import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { buildSeo, humanizeSlug } from "@/lib/seo";
import { gameOgImage } from "@/lib/og-images";
import { GameResultDateView } from "@/components/GameResultDateView";
import { breadcrumbSchema, faqPageSchema } from "@/lib/jsonld";
import { getResultsForGame, isSaneResultDate, getSafeTodayIso } from "@/data/results";
import { drawNavigation, drawSocialMeta, findRouteGame, resultFaqs } from "@/lib/results-nav";
import { canonicalUrl, isArchivedResultPath } from "@/lib/seo-policy";
import { formatDate } from "@/lib/format";
import { gamePaths } from "@/lib/game-routing";
import { STATES } from "@/data/states";

export const Route = createFileRoute("/$stateSlug/$gameSlug/results/$date")({
  loader: ({ params }) => {
    const state = STATES.find((candidate) => candidate.slug === params.stateSlug);
    const game = findRouteGame(params.gameSlug, params.stateSlug);
    if (!game || !state || !isSaneResultDate(params.date) || params.date > getSafeTodayIso()) {
      throw notFound();
    }
    const draw = game
      ? getResultsForGame(game.id, 200).find((result) => result.draw_date === params.date)
      : undefined;
    if (!draw) {
      // Valid past date outside the archive: 301 to the game's history page
      // rather than rendering an empty page Google flags as a soft 404.
      throw redirect({
        to: "/$stateSlug/$gameSlug/history",
        params: { stateSlug: params.stateSlug, gameSlug: params.gameSlug },
        statusCode: 301,
        replace: true,
      });
    }
    const nav = drawNavigation(game.id, params.date);
    return {
      date: params.date,
      gameName: game.name,
      stateName: state.name,
      drawSchedule: game.draw_schedule ?? null,
      numbers: draw.winning_numbers,
      bonus: draw.bonus_numbers,
      multiplier: draw.multiplier,
      prev: nav.prev,
      next: nav.next,
    };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {
      meta: [
        { title: "Lottery result unavailable — USA Lottery Club" },
        { name: "robots", content: "noindex, nofollow" },
      ],
    };
    const name = loaderData.gameName || humanizeSlug(params.gameSlug);
    const state = loaderData.stateName || humanizeSlug(params.stateSlug);
    const paths = gamePaths(params.gameSlug, params.stateSlug);
    const path = paths.result(params.date);
    const dateLabel = formatDate(params.date);
    const numbers = loaderData.numbers ?? [];
    const bonus = loaderData.bonus ?? [];
    const numbersText = numbers.join(", ");
    const seo = buildSeo({
      title: `${state} ${name} Numbers — ${params.date}`,
      description: `${state} ${name} winning numbers for ${dateLabel}: ${numbersText || "see the full result"}. Bonus numbers, multiplier, previous draws, and the full ${name} archive.`,
      path,
      type: "article",
      image: gameOgImage(params.gameSlug, params.stateSlug),
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: `${state} ${name} Winning Numbers — ${dateLabel}`,
          datePublished: params.date,
          dateModified: params.date,
          mainEntityOfPage: path,
          about: { "@type": "Game", name: `${state} ${name}` },
        },
        breadcrumbSchema([
          { name: "Home", item: "/" },
          { name: state, item: `/states/${params.stateSlug}` },
          { name, item: paths.game },
          { name: "Results history", item: paths.history },
          { name: dateLabel, item: path },
        ]),
        faqPageSchema(
          resultFaqs({
            gameName: name,
            dateLabel,
            stateName: state,
            drawSchedule: loaderData.drawSchedule ?? null,
            numbers,
            bonus,
            multiplier: loaderData.multiplier,
          }),
        ),
      ],
    });
    return {
      ...seo,
      meta: [
        ...seo.meta,
        // Archived draws: crawlable and link-passing, but not index candidates.
        ...(isArchivedResultPath(path) ? [{ name: "robots", content: "noindex, follow" }] : []),
        ...drawSocialMeta({
          scope: name.toLowerCase().includes(state.toLowerCase()) ? name : `${state} ${name}`,
          date: params.date,
          dateLabel,
          numbers,
          bonus,
          multiplier: loaderData.multiplier,
        }),
      ],
      links: [
        ...seo.links,
        ...(loaderData.prev ? [{ rel: "prev", href: canonicalUrl(paths.result(loaderData.prev)) }] : []),
        ...(loaderData.next ? [{ rel: "next", href: canonicalUrl(paths.result(loaderData.next)) }] : []),
      ],
    };
  },
  component: StateGameResultRoute,
});

function StateGameResultRoute() {
  const { stateSlug, gameSlug, date } = Route.useParams();
  return <GameResultDateView gameSlug={gameSlug} date={date} stateSlug={stateSlug} />;
}
